import React from 'react';
import { motion } from 'framer-motion';
import { LANDING } from '@/constants/testIds';
import { scrollToId } from '@/hooks/useLenis';

const tiers = [
    {
        label: 'Lanzamiento',
        sub: 'Solo 8 cupos · hasta 30 Sep',
        price: 1500,
        highlight: true,
        badge: 'Mejor precio',
        testId: LANDING.pricingCardLanzamiento,
    },
    {
        label: 'Early Bird',
        sub: 'Hasta 30 Oct',
        price: 1600,
        testId: LANDING.pricingCardEarlybird,
    },
    {
        label: 'Regular',
        sub: 'Hasta 30 Nov',
        price: 1750,
        testId: LANDING.pricingCardRegular,
    },
    {
        label: 'Last Call',
        sub: 'Hasta 19 Dic',
        price: 2200,
        testId: LANDING.pricingCardLastcall,
    },
];

const Pricing = () => (
    <section id="inversion" className="relative py-16 md:py-20 border-t border-white/10 overflow-hidden">
        <div className="pointer-events-none absolute top-1/2 left-0 h-[500px] w-[500px] rounded-full -translate-y-1/2" style={{ background: 'radial-gradient(closest-side, rgba(225,29,72,0.15), transparent 70%)' }} />

        <div className="relative mx-auto max-w-[1440px] px-6 md:px-10">
            <div className="grid grid-cols-12 gap-5 md:gap-8 mb-8">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                    className="col-span-12 md:col-span-8"
                >
                    <div className="eyebrow-brand mb-6">Precios</div>
                    <h2 className="font-display text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl">
                        Cada mes que esperas,<br />
                        <span className="italic-accent text-[color:var(--brand)] font-normal">tu inversión sube.</span>
                    </h2>
                </motion.div>
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7, delay: 0.1 }}
                    className="col-span-12 md:col-span-4 md:pt-4"
                >
                    <p className="text-white/75 text-lg leading-relaxed">
                        Valor regular del programa: USD 2.200 (impuestos incluidos).
                        Reserva con USD 300 y el resto en cuotas personalizadas bajo contrato.
                        Cuanto antes entres a la Tribu, más flexible tu plan de pagos.
                    </p>
                </motion.div>
            </div>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="mb-10 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6 border-l-2 border-[color:var(--amber)] bg-white/[0.03] p-5 md:p-6"
            >
                <div className="text-xs md:text-sm text-white/70 leading-relaxed max-w-[70ch]">
                    Estás invirtiendo en <strong className="text-white">meses de consultoría de internacionalización</strong> —
                    diagnóstico, agenda B2B, ejecución en Madrid y seguimiento post-feria.
                    El pasaje aéreo lo pones tú; nosotros ponemos el retorno.
                </div>
            </motion.div>

            {/* Staircase */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                {tiers.map((t, i) => (
                    <motion.div
                        key={t.label}
                        data-testid={t.testId}
                        initial={{ opacity: 0, y: 60 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        whileHover={{ y: -8 }}
                        viewport={{ once: true, margin: '-80px' }}
                        transition={{ duration: 0.8, delay: i * 0.1 }}
                        className={`relative p-8 border ${t.highlight ? 'bg-[color:var(--surface)] border-[color:var(--brand)]' : 'bg-[color:var(--bg)] border-white/12'} flex flex-col justify-between hover:border-[color:var(--brand)]`}
                        style={{ minHeight: `${280 + i * 40}px` }}
                    >
                        {t.badge && (
                            <div className="absolute -top-3 left-6 rounded-full bg-[color:var(--brand)] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-white">
                                {t.badge}
                            </div>
                        )}
                        <div>
                            <div className="overline text-white/50 mb-3">Tramo {String(i + 1).padStart(2, '0')}</div>
                            <div className="font-display text-white text-3xl mb-2">{t.label}</div>
                            <div className="text-xs uppercase tracking-[0.18em] text-white/50">{t.sub}</div>
                        </div>
                        <div className="mt-8">
                            <div className="flex items-baseline gap-2">
                                <span className="text-xs text-white/50 font-semibold">USD</span>
                                <span className="num-display text-white text-4xl md:text-5xl">
                                    {t.price.toLocaleString('de-DE')}
                                </span>
                            </div>
                            <div className="mt-2 text-[10px] uppercase tracking-[0.2em] text-white/40">
                                Impuestos incluidos
                            </div>
                        </div>
                        {t.highlight && (
                            <div className="pointer-events-none absolute -bottom-24 -right-24 h-48 w-48 rounded-full blur-3xl" style={{ background: 'rgba(225,29,72,0.4)' }} />
                        )}
                    </motion.div>
                ))}
            </div>

            <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="mt-14 grid grid-cols-12 gap-5 md:gap-8 items-center border-t border-white/10 pt-10"
            >
                <div className="col-span-12 md:col-span-7">
                    <div className="eyebrow-brand mb-3">Plan de pagos flexible</div>
                    <p className="text-white text-xl md:text-2xl leading-snug max-w-[52ch]">
                        Reserva con <span className="text-[color:var(--brand)] font-bold">USD 300</span> y
                        el saldo en <span className="text-[color:var(--brand)] font-bold">cuotas personalizadas</span>,
                        establecidas bajo contrato.
                    </p>
                </div>
                <div className="col-span-12 md:col-span-5 md:text-right">
                    <button
                        data-testid={LANDING.pricingCtaBook}
                        onClick={() => scrollToId('sesion')}
                        className="btn-primary"
                    >
                        Agendar sesión y aplicar
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                </div>
            </motion.div>
        </div>
    </section>
);

export default Pricing;
